import {
  memo,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  createElement,
  useRef,
} from "react";
import { sort } from "fast-sort";
import { make as Tooltip } from "common/src/components/Tooltip.bs";
import ArrowUpIcon from "@mui/icons-material/ArrowUpward";
import { css } from "@emotion/css";
import { use as useScreenSize } from "common/src/atoms/ScreenAtom.bs";
import { useT } from "common/src/bindings/Translation.bs";
import { use as useSet } from "common/src/data/TftSet.bs";
import { useValue as useAperture } from "../ctx/ApertureCtx.bs";
import { useVirtualizer, useWindowVirtualizer } from "@tanstack/react-virtual";

type D = any;
type Ctx = any;
type size = "sm" | "md" | "lg";
type TblCtx = {
  t: (key: string) => string;
  unitsT: (key: string) => string;
  traitsT: (key: string) => string;
  itemsT: (key: string) => string;
  augmentsT: (key: string) => string;
  set: number;
  size: size;
};

type Col = {
  defaultDesc: boolean;
  alignRight: boolean;
  titleClass: string;
  renderTitle: (ctx: Ctx, tblCtx: TblCtx) => JSX.Element;
  valueClass: string;
  renderValue: (d: D, ctx: Ctx, tblCtx: TblCtx) => JSX.Element;
  order?: (d: D, ctx: Ctx, tblCtx: TblCtx) => any;
  tooltip?: (d: D, ctx: Ctx, tblCtx: TblCtx) => JSX.Element;
  titleTooltip?: JSX.Element;
  width: number;
  widthPct?: number;
  widthClass: string;
  rightBorder: boolean;
  href?: (d: D) => string;
};

type ColParams = {
  defaultDesc?: boolean;
  alignRight?: boolean;
  renderTitle: (ctx: Ctx, tblCtx: TblCtx) => JSX.Element;
  titleClass?: string;
  renderValue: (d: D, ctx: Ctx, tblCtx: TblCtx) => JSX.Element;
  valueClass?: string;
  tooltip?: (d: D, ctx: Ctx, tblCtx: TblCtx) => JSX.Element;
  titleTooltip?: JSX.Element;
  order?: (d: D, ctx: Ctx, tblCtx: TblCtx) => any;
  width: number;
  rightBorder?: boolean;
  href?: (d: D) => string;
};

export const makeCol = ({
  defaultDesc,
  alignRight = false,
  renderTitle,
  titleClass = "",
  renderValue,
  width,
  valueClass = "",
  order,
  tooltip,
  titleTooltip,
  rightBorder = true,
  href,
}: ColParams): Col => {
  const widthClass = css({
    width: width + "px",
  });

  return {
    defaultDesc: defaultDesc == null ? true : defaultDesc,
    alignRight: alignRight,
    renderTitle: renderTitle,
    titleClass: titleClass,
    renderValue: renderValue,
    width: width,
    widthPct: undefined,
    valueClass: valueClass,
    order: order,
    tooltip: tooltip,
    titleTooltip,
    widthClass,
    rightBorder,
    href,
  };
};

export function syncScroll() {
  const header = document.getElementById("tbl-header");
  const body = document.getElementById("tbl-body");

  const cb = (e: any) => {
    header?.scroll(e.target.scrollLeft, 0);
  };
  body?.addEventListener("scroll", cb);
}

const colPadClass = (size: size) => {
  switch (size) {
    case "sm":
      return "px-[12px]";
    case "md":
      return "px-[14px]";
    case "lg":
      return "px-[16px]";
    default:
      return "";
  }
};

const lastStickyColPadClass = (size: size) => {
  switch (size) {
    case "sm":
      return "px-[6px] md:px-[12px]";
    case "md":
      return "px-[8px] md:px-[14px]";
    case "lg":
      return "px-[10px] md:px-[16px]";
    default:
      return "";
  }
};

type RowProps = {
  size: size;
  cols: Array<Col>;
  d: D;
  context: Ctx;
  sticky?: boolean;
  tableCtx: TblCtx;
};

export const Row = memo(
  ({ size, cols, d, context, sticky, tableCtx }: RowProps) => {
    return (
      <>
        {cols.map((col, idx) => {
          const padClass = colPadClass(size);
          const mainClass =
            sticky && idx == cols.length - 1
              ? lastStickyColPadClass(size)
              : `${padClass}`;

          const el = createElement(
            col.href != null ? "a" : "div",
            {
              key: idx,
              href: col.href && col.href(d),
              className: `flex items-center ${
                col.alignRight ? "justify-end" : ""
              } ${col.valueClass} ${mainClass} ${col.widthClass} ${
                (idx < cols.length - 1 || sticky) && col.rightBorder
                  ? "tbl-cell-right-border"
                  : ""
              }`,
            },
            col.renderValue(d, context, tableCtx),
          );

          if (col.tooltip != null) {
            return (
              <Tooltip key={idx} title={col.tooltip(d, context, tableCtx)}>
                {el}
              </Tooltip>
            );
          } else {
            return el;
          }
        })}
      </>
    );
  },
);

type BodyProps = {
  size: size;
  cols: Array<Col>;
  data: Array<D>;
  keyFn: (d: D) => string;
  context: Ctx;
  tableCtx: TblCtx;
  rowClass: undefined | ((d: D) => string);
  stickyColCount: number;
  visibleFilter?: (d: D) => boolean;
  scrollEl?: [any, number];
};

export const Body = memo(
  ({
    size,
    cols,
    data,
    keyFn,
    context,
    tableCtx,
    rowClass,
    stickyColCount,
    visibleFilter,
    scrollEl,
  }: BodyProps) => {
    const screenSize = useScreenSize();
    const [leftCols, rightCols] = useMemo(
      () => [cols.slice(0, stickyColCount), cols.slice(stickyColCount)],
      [cols, stickyColCount],
    );

    const leftSideWidth = leftCols.reduce((acc, col) => acc + col.width, 0);
    const rightSideWidth = rightCols.reduce((acc, col) => acc + col.width, 0);

    const leftSideClass = css({
      minWidth: `${leftSideWidth - 12 + 4}px`,
      maxWidth: `${leftSideWidth - 12 + 4}px`,
      "@media (min-width: 910px)": {
        minWidth: `${leftSideWidth + 4}px`,
        maxWidth: `${leftSideWidth + 4}px`,
      },
    });

    const rightSideClass = css({
      maxWidth: `calc(100vw - ${
        leftSideWidth == 0 ? 0 : leftSideWidth + 4 - 12
      }px)`,
      "@media (min-width: 910px)": {
        maxWidth: `calc(100vw - ${
          leftSideWidth == 0 ? 0 : leftSideWidth + 4
        }px)`,
      },
    });

    const visibleRows = useMemo(() => {
      return data.filter((d) => visibleFilter == null || visibleFilter(d));
    }, [data, visibleFilter]);

    let rowVirtualizer;
    const bodyRef = useRef<null | HTMLDivElement>(null);
    const [scrollMargin, setScrollMargin] = useState(() =>
      scrollEl != null ? scrollEl[1] : 250,
    );
    let rowSize: number;
    if (size === "lg") {
      rowSize = 52;
    } else if (size === "md") {
      rowSize = 46;
    } else {
      rowSize = 39;
    }

    if (scrollEl != null) {
      rowVirtualizer = useVirtualizer({
        count: visibleRows.length,
        estimateSize: () => rowSize,
        overscan: 5,
        scrollMargin: scrollMargin,
        getScrollElement: () => scrollEl[0],
      });
    } else {
      rowVirtualizer = useWindowVirtualizer({
        count: visibleRows.length,
        estimateSize: () => rowSize,
        scrollMargin: scrollMargin,
        initialRect: { width: 350, height: 1000 },
        overscan: 5,
      });

      useLayoutEffect(() => {
        if (bodyRef.current) {
          const rect = bodyRef.current.getBoundingClientRect();
          setScrollMargin(rect.top + window.scrollY);
        }
      }, [screenSize]);
    }

    return (
      <div className="max-w-[100vw]">
        <div className="flex w-full">
          {stickyColCount === 0 ? null : (
            <div
              className={`sticky left-0 z-10 tbl-body-inner-${size} pl-[4px] ${leftSideClass}`}
            >
              {data.map((d) => {
                const visible = visibleFilter == null || visibleFilter(d);
                const sizeClass = `tbl-row-${size}`;
                const rc = rowClass ? rowClass(d) : "";
                return (
                  <div
                    key={keyFn(d)}
                    className={`${sizeClass} ${rc} ${
                      visible ? "" : "tbl-row-hidden"
                    }  flex`}
                  >
                    <Row
                      size={size}
                      cols={leftCols}
                      d={d}
                      context={context}
                      sticky
                      tableCtx={tableCtx}
                    />
                  </div>
                );
              })}
            </div>
          )}
          <div
            ref={bodyRef}
            id="tbl-body"
            className={`overflow-x-auto flex-1 tbl-body-inner-${size} ${rightSideClass}`}
            style={{
              height: `${Math.max(0, rowVirtualizer.getTotalSize())}px`,
              position: "relative",
              width: `${rightSideWidth}px`,
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualItem) => {
              const d = visibleRows[virtualItem.index];
              const sizeClass = `tbl-row-${size}`;
              const rc = rowClass ? rowClass(d) : "";
              return (
                <div
                  key={virtualItem.key}
                  className={`${rc} flex ${sizeClass} ${
                    stickyColCount === 0 ? "pl-[4px]" : ""
                  } pr-[4px]`}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    minWidth: `${rightSideWidth}px`,
                    width: "100%",
                    height: `${virtualItem.size}px`,
                    transform: `translateY(${
                      virtualItem.start - scrollMargin
                    }px)`,
                  }}
                >
                  <Row
                    size={size}
                    cols={rightCols}
                    d={d}
                    context={context}
                    sticky={false}
                    tableCtx={tableCtx}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  },
);

const ctSizeClass = (size: size) => {
  switch (size) {
    case "sm":
      return "h-[41px]";
    case "md":
      return "h-[48px]";
    case "lg":
      return "h-[54px]";
  }
};

const sortColPadClass = (size: size, alignRight: boolean) => {
  switch (size) {
    case "sm":
      return alignRight ? "pr-[12px]" : "pl-[12px]";
    case "md":
      return alignRight ? "pr-[14px]" : "pl-[14px]";
    case "lg":
      return alignRight ? "pr-[16px]" : "pl-[16px]";
    default:
      return "";
  }
};

const lastStickySortColPadClass = (size: size, alignRight: boolean) => {
  switch (size) {
    case "sm":
      return alignRight ? "pr-[6px] md:pr-[12px]" : "pl-[6px] md:pl-[12px]";
    case "md":
      return alignRight ? "pr-[8px] md:pr-[14px]" : "pl-[8px] md:pl-[14px]";
    case "lg":
      return alignRight ? "pr-[10px] md:pr-[16px]" : "pl-[10px] md:pl-[16px]";
    default:
      return "";
  }
};

const arrowFontSize = (size: size) => {
  switch (size) {
    case "sm":
      return "text-[16px]";
    case "md":
      return "text-[18px]";
    case "lg":
      return "text-[20px]";
    default:
      return "";
  }
};

type ColTitlesInnerProps = {
  size: size;
  cols: Array<Col>;
  context: Ctx;
  onSort: (n: number) => void;
  sortOrder: undefined | [number, boolean];
  sticky?: boolean;
  baseIdx: number;
  tableCtx: TblCtx;
};

export const ColTitlesInner = memo(
  ({
    size,
    cols,
    context,
    onSort,
    sortOrder,
    sticky,
    baseIdx,
    tableCtx,
  }: ColTitlesInnerProps) => {
    return (
      <div className="h-full flex items-center select-none">
        {cols.map((col, idx) => {
          const arrow =
            sortOrder != null
              ? sortOrder[0] === baseIdx + idx
                ? sortOrder[1]
                : undefined
              : undefined;

          const padClass =
            sticky && idx == cols.length - 1
              ? arrow == null
                ? lastStickyColPadClass(size)
                : lastStickySortColPadClass(size, col.alignRight)
              : arrow == null
              ? colPadClass(size)
              : sortColPadClass(size, col.alignRight);

          const colClass = `flex items-center ${padClass} ${col.titleClass} ${
            col.widthClass
          } ${col.alignRight ? "justify-end" : ""} ${
            col.order != null ? "cursor-pointer" : ""
          }`;
          const onClick =
            col.order != null ? () => onSort(baseIdx + idx) : undefined;

          // Need to wrap in a span here cause otherwise google translate breaks react
          const contentEl = <span>{col.renderTitle(context, tableCtx)}</span>;

          return (
            <div
              key={idx}
              className={
                colClass +
                ` h-full ${
                  (idx < cols.length - 1 || sticky) && col.rightBorder
                    ? "tbl-cell-right-border"
                    : ""
                }`
              }
              onClick={onClick}
            >
              {arrow != null && (
                <div>
                  <div
                    className={`${arrowFontSize(
                      size,
                    )} text-primary-11 mr-[1px] transition-transform ${
                      arrow ? "" : "rotate-180"
                    }`}
                  >
                    <ArrowUpIcon
                      viewBox="0 2 24 24"
                      fontSize="inherit"
                      color="inherit"
                    />
                  </div>
                </div>
              )}
              {col.titleTooltip ? (
                <Tooltip title={col.titleTooltip}>{contentEl}</Tooltip>
              ) : (
                contentEl
              )}
            </div>
          );
        })}
      </div>
    );
  },
);

type ColTitlesProps = {
  size: size;
  cols: Array<Col>;
  context: Ctx;
  onSort: (idx: number) => void;
  sortOrder: undefined | [number, boolean];
  stickyColCount: number;
  montserratHeader: boolean;
  tableCtx: TblCtx;
};

export const ColTitles = memo(
  ({
    size,
    cols,
    context,
    onSort,
    sortOrder,
    stickyColCount,
    montserratHeader,
    tableCtx,
  }: ColTitlesProps) => {
    const [leftCols, rightCols] = useMemo(
      () => [cols.slice(0, stickyColCount), cols.slice(stickyColCount)],
      [cols, stickyColCount],
    );

    const leftSideWidth = leftCols.reduce((acc, col) => acc + col.width, 0);
    // const rightSideWidth = rightCols.reduce((acc, col) => acc + col.width, 0);

    const leftSideClass = css({
      maxWidth: `${leftSideWidth - 12}px`,
      "@media (min-width: 910px)": {
        maxWidth: `${leftSideWidth}px`,
      },
    });

    const rightSideClass = css({
      maxWidth: `calc(100vw - ${
        leftSideWidth == 0 ? 0 : leftSideWidth - 12
      }px)`,
      "@media (min-width: 910px)": {
        maxWidth: `calc(100vw - ${leftSideWidth == 0 ? 0 : leftSideWidth}px)`,
      },
    });

    return (
      <div
        className={`${ctSizeClass(size)} pl-[4px] pr-[4px] ${
          montserratHeader ? "font-semibold" : "font-roboto"
        } flex items-center`}
      >
        {stickyColCount == 0 ? null : (
          <div className={"h-full " + leftSideClass}>
            <ColTitlesInner
              size={size}
              cols={leftCols}
              context={context}
              onSort={onSort}
              sortOrder={sortOrder}
              sticky={true}
              baseIdx={0}
              tableCtx={tableCtx}
            />
          </div>
        )}
        <div
          id="tbl-header"
          className={`h-full flex-1 overflow-hidden ${rightSideClass}`}
        >
          <ColTitlesInner
            size={size}
            cols={rightCols}
            context={context}
            onSort={onSort}
            sortOrder={sortOrder}
            sticky={false}
            baseIdx={stickyColCount}
            tableCtx={tableCtx}
          />
        </div>
      </div>
    );
  },
);

const getWrapperClass = (size: size) => {
  switch (size) {
    case "sm":
      return "text-[13px] leading-[15px]";
    case "md":
      return "text-[15px] leading-[22px]";
    case "lg":
      return "text-[17px] leading-[28px]";
  }
};

const makeCols = (cols: Array<Col>, stickyColCount: number): Array<Col> => {
  let fullWidth = cols.reduce((acc, col, idx) => {
    if (idx >= stickyColCount) {
      return acc + col.width;
    } else {
      return acc;
    }
  }, 0);

  return cols.map((col, idx) => {
    if (idx >= stickyColCount) {
      const widthPct = (100 * col.width) / fullWidth;
      const widthClass = css({
        minWidth: col.width + "px",
        width: widthPct + "%",
      });

      return {
        ...col,
        widthPct: widthPct,
        widthClass: widthClass,
      };
    } else if (idx === stickyColCount - 1) {
      return {
        ...col,
        widthClass: css({
          width: col.width - 12 + "px",
          "@media (min-width: 910px)": {
            width: col.width + "px",
          },
        }),
      };
    } else {
      return col;
    }
  });
};

let useRenderElements = (lazyInit: boolean) => {
  let [renderElements, setRenderElements] = useState(lazyInit ? 50 : 1000);
  let throttle = useRef(false);

  useEffect(() => {
    const screenWidth =
      typeof window === "undefined"
        ? 1920
        : document.documentElement.clientWidth;
    if (screenWidth < 910) {
      const cb = () => {
        const sh = document.documentElement.scrollHeight;
        const scrollY = window.scrollY;
        const ih = window.innerHeight;
        if (sh - (scrollY + ih) < 500) {
          if (!throttle.current) {
            setRenderElements((re) => {
              if (re < 1000) {
                return re + 50;
              } else {
                return re;
              }
            });
            throttle.current = true;
            setTimeout(() => {
              throttle.current = false;
              cb();
            }, 350);
          }
        }
      };

      window.addEventListener("scroll", cb, { passive: true });
      const ro = new ResizeObserver(cb);
      ro.observe(document.body);

      return () => {
        window.removeEventListener("scroll", cb);
        ro.disconnect();
      };
    } else {
      if (renderElements < 1000) {
        setRenderElements(300);
        setTimeout(() => {
          setRenderElements(600);
          setTimeout(() => {
            setRenderElements(1000);
          }, 1000);
        }, 1000);
      }
    }
  }, []);

  return renderElements;
};

type top = "t0" | "t48" | "t52" | "t262" | "t91" | "t92" | "tm40" | "t47m92";

const headerTop = (top: top): string => {
  switch (top) {
    case "t262":
      return "top-[262px]";
    case "t92":
      return "top-[92px]";
    case "t91":
      return "top-[91px]";
    case "t52":
      return "top-[52px]";
    case "t48":
      return "top-[48px]";
    case "t47m92":
      return "top-[47px] md:top-[92px]";
    case "t0":
      return "top-[0px]";
    case "tm40":
      return "top-[-48px]";
  }
};

const shadowHeaderTop = (top: top): string => {
  switch (top) {
    case "t262":
      return "top-[279px]";
    case "t92":
      return "top-[109px]";
    case "t91":
      return "top-[108px]";
    case "t52":
      return "top-[69px]";
    case "t48":
      return "top-[65px]";
    case "t47m92":
      return "top-[64px] md:top-[109px]";
    case "t0":
      return "top-[17px]";
    case "tm40":
      return "top-[-31px]";
  }
};

type StatsTableProps = {
  size?: size;
  stickyColCount?: number;
  cols: Array<Col>;
  data: Array<D>;
  context?: Ctx;
  rowClass?: (d: D) => string;
  extraStickyHeader?: JSX.Element;
  keyFn: (d: D) => string;
  top?: top;
  montserratHeader?: boolean;
  defaultSortIdx?: number;
  visibleFilter?: (d: D) => boolean;
  lazyInit?: boolean;
  scrollEl?: [any, number];
};

export const StatsTable = memo(
  ({
    size: baseSize,
    stickyColCount: baseStickyColCount,
    cols: baseCols,
    rowClass,
    data: baseData,
    context,
    extraStickyHeader,
    keyFn,
    top: baseTop,
    defaultSortIdx,
    montserratHeader = false,
    visibleFilter,
    lazyInit: lazyInitBase,
    scrollEl,
  }: StatsTableProps) => {
    const t = useT(0);
    const unitsT = useT(1);
    const traitsT = useT(2);
    const itemsT = useT(3);
    const augmentsT = useT(4);
    const set = useSet();
    const aperture = useAperture();
    const size = baseSize || "md";
    const lazyInit = lazyInitBase != null ? lazyInitBase : true;

    const tableCtx = useMemo(
      () => ({
        t,
        unitsT,
        traitsT,
        itemsT,
        augmentsT,
        set,
        size,
        aperture,
      }),
      [t, unitsT, traitsT, augmentsT, set, size, aperture],
    );

    const renderElements = useRenderElements(lazyInit);
    const top = baseTop || "t91";
    const stickyColCount = baseStickyColCount || 0;

    const cols = useMemo(
      () => makeCols(baseCols, stickyColCount),
      [baseCols, stickyColCount],
    );

    const [sortOrder, setSortOrder] = useState<[number, boolean] | undefined>(
      () => {
        if (defaultSortIdx != null) {
          const col = cols[defaultSortIdx];
          if (col != null && col.order != null) {
            return [defaultSortIdx, col.defaultDesc];
          }
        }
        const idx = cols.findIndex((col: Col) => col.order != null);
        return idx === -1 ? undefined : [idx, cols[idx].defaultDesc];
      },
    );

    useEffect(() => {
      syncScroll();
    }, []);

    const onSort = useCallback(
      (idx: number) => {
        setSortOrder((sortOrder) => {
          if (sortOrder == null) {
            return [idx, cols[idx].defaultDesc];
          } else {
            const order =
              sortOrder[0] === idx ? !sortOrder[1] : cols[idx].defaultDesc;
            return [idx, order];
          }
        });
      },
      [cols],
    );

    const sortedData = useMemo(() => {
      if (sortOrder == null) {
        return baseData;
      }
      const [colIdx, sortDesc] = sortOrder;
      const col = cols[colIdx];
      if (col == null || col.order == null) {
        return baseData;
      }
      if (sortDesc) {
        return sort(baseData).desc((v) => col.order!(v, context, tableCtx));
      } else {
        return sort(baseData).asc((v) => col.order!(v, context, tableCtx));
      }
    }, [baseData, sortOrder]);

    const data = useMemo(
      () => sortedData.slice(0, renderElements),
      [sortedData, renderElements],
    );

    return (
      <div className={`font-medium font-montserrat ${getWrapperClass(size)}`}>
        <div
          className={`max-w-[100vw] sticky z-[20] h-[30px] bg-primary-12 ${shadowHeaderTop(
            top,
          )} mt-[7px] elevation-6`}
        />
        <div
          className={`max-w-[100vw] sticky-header ${headerTop(
            top,
          )} overflow-visible mt-[-37px]`}
        >
          {extraStickyHeader}
          <ColTitles
            montserratHeader={montserratHeader}
            cols={cols}
            context={context}
            size={size}
            onSort={onSort}
            sortOrder={sortOrder}
            stickyColCount={stickyColCount}
            tableCtx={tableCtx}
          />
        </div>
        <Body
          size={size}
          cols={cols}
          data={data}
          keyFn={keyFn}
          context={context}
          tableCtx={tableCtx}
          rowClass={rowClass}
          stickyColCount={stickyColCount}
          visibleFilter={visibleFilter}
          scrollEl={scrollEl}
        />
      </div>
    );
  },
);
